import { refreshSession } from "./auth-api";
import { isDefinitiveRejection, mayRetainAfterTransportFailure } from "./refresh-error";
import { getConfig, getConfiguredSessionNamespace } from "../client/config";
import { refreshedTokenMayReplaceStored } from "./session-binding";
import { notifySessionCleared } from "./session-events";
import { decideCommit, mintSessionGeneration, withSessionLock, } from "./session-mutation";
export class WebSessionManager {
    static scopedStorageKey() {
        return 'bounded_session_storage:v2:' + getConfiguredSessionNamespace();
    }
    static get BOUNDED_SESSION_STORAGE_KEY() {
        const scoped = this.scopedStorageKey();
        return localStorage.getItem(scoped + ':scope') === 'app' || localStorage.getItem(scoped) !== null
            ? scoped : 'bounded_session_storage';
    }
    static logoutKey() { return this.scopedStorageKey() + ':logout'; }
    /** Capture before login's network work; checked under the session mutation lock. */
    static captureSessionEstablishment() {
        var _a, _b;
        const namespace = getConfiguredSessionNamespace();
        const epoch = this.establishmentEpoch;
        const generation = (_b = (_a = this.readRaw()) === null || _a === void 0 ? void 0 : _a.sessionGeneration) !== null && _b !== void 0 ? _b : null;
        const logout = localStorage.getItem(this.logoutKey());
        return () => {
            var _a, _b;
            if (namespace !== getConfiguredSessionNamespace() || epoch !== this.establishmentEpoch
                || generation !== ((_b = (_a = this.readRaw()) === null || _a === void 0 ? void 0 : _a.sessionGeneration) !== null && _b !== void 0 ? _b : null)
                || logout !== localStorage.getItem(this.logoutKey()))
                throw new Error('session_establishment_superseded');
        };
    }
    /** Raw read of the stored blob. Callers inside the lock use this, never a cached copy. */
    static readRaw() {
        if (typeof window === "undefined")
            return null;
        const raw = localStorage.getItem(this.BOUNDED_SESSION_STORAGE_KEY);
        if (!raw)
            return null;
        try {
            return JSON.parse(raw);
        }
        catch (_a) {
            return null;
        }
    }
    static writeRaw(sessionObj) {
        localStorage.setItem(this.BOUNDED_SESSION_STORAGE_KEY, JSON.stringify(sessionObj));
    }
    /**
     * Read the session, stamping a `sessionGeneration` if it predates them.
     *
     * Sessions written before generations existed have no lineage marker, so a
     * later refresh could not prove it descended from them. Minting on read
     * upgrades them in place; a request already in flight across that mint carries
     * no generation and fails closed at commit rather than writing unprovably.
     */
    static async readSessionWithGeneration() {
        const existing = this.readRaw();
        if (!existing)
            return null;
        if (existing.sessionGeneration)
            return existing;
        return await withSessionLock(() => {
            // Re-read inside the lock: another tab may have minted one already.
            const current = this.readRaw();
            if (!current)
                return null;
            if (current.sessionGeneration)
                return current;
            const upgraded = Object.assign(Object.assign({}, current), { sessionGeneration: mintSessionGeneration() });
            this.writeRaw(upgraded);
            return upgraded;
        });
    }
    /**
     * Decode a base64url-encoded string (used by JWT payloads).
     * Normalises base64url → standard base64 before calling browser atob().
     */
    static decodeBase64Url(input) {
        let b64 = input.replace(/-/g, '+').replace(/_/g, '/');
        const pad = b64.length % 4;
        if (pad === 2)
            b64 += '==';
        else if (pad === 3)
            b64 += '=';
        return atob(b64);
    }
    /* ------------------------------------------------------------------ */
    /* STORE                                                              */
    /* ------------------------------------------------------------------ */
    static async storeSession(address, accessToken, idToken, refreshToken, issuer, beforeCommit) {
        var _a;
        if (typeof window === "undefined")
            return;
        let appScoped = false;
        // JWT-wallet binding: refuse to store a session whose idToken is bound
        // to a different wallet than `address`. Prevents races that would otherwise
        // leave localStorage with mismatched address/token state.
        try {
            const payloadB64 = idToken.split(".")[1];
            if (payloadB64) {
                const payload = JSON.parse(this.decodeBase64Url(payloadB64));
                appScoped = payload["custom:walletScope"] === "app";
                const tokenWallet = payload["custom:walletAddress"];
                if (tokenWallet && tokenWallet !== address) {
                    throw new Error(`[WebSessionManager] Refusing to store session: address (${address}) does not match idToken custom:walletAddress (${tokenWallet})`);
                }
                if (!tokenWallet) {
                    console.warn("[WebSessionManager] storeSession: idToken has no custom:walletAddress claim — writing without validation");
                }
            }
        }
        catch (err) {
            if (typeof (err === null || err === void 0 ? void 0 : err.message) === "string" && err.message.includes("Refusing to store session")) {
                throw err;
            }
            console.warn("[WebSessionManager] storeSession: failed to decode idToken for validation. reason:", (_a = err === null || err === void 0 ? void 0 : err.name) !== null && _a !== void 0 ? _a : "decode_failed");
        }
        const config = await getConfig();
        const currentAppId = config.appId;
        const namespace = getConfiguredSessionNamespace();
        const storageKey = appScoped || localStorage.getItem(this.scopedStorageKey() + ":scope") === "app"
            ? this.scopedStorageKey() : "bounded_session_storage";
        // The auth issuer base that minted this session, so silent refresh POSTs to
        // the RIGHT /session/refresh. Email overrides with humanAuthApiUrl; all
        // wallet/guest/signature flows default to authApiUrl (the bounded-auth issuer).
        const sessionIssuer = (issuer || config.authApiUrl || "").replace(/\/$/, "");
        // A login always starts a NEW generation. Refresh commits preserve it, so
        // "same generation" means "same login", which is what lets a post-refresh
        // retry be told apart from a re-login by the same wallet.
        const generation = mintSessionGeneration();
        await withSessionLock(() => {
            beforeCommit === null || beforeCommit === void 0 ? void 0 : beforeCommit();
            if (namespace !== getConfiguredSessionNamespace())
                throw new Error("session_establishment_superseded");
            ++this.establishmentEpoch;
            if (appScoped)
                localStorage.setItem(this.scopedStorageKey() + ":scope", "app");
            localStorage.setItem(storageKey, JSON.stringify(Object.assign({ address,
                accessToken,
                idToken,
                refreshToken, appId: currentAppId, sessionGeneration: generation }, (sessionIssuer ? { issuer: sessionIssuer } : {}))));
        });
        // Adoption can fail after persistence (for example, logout while the
        // provider is loading). Remove only this incomplete login, even if the
        // caller has since switched app configuration or another tab signed in.
        return async () => {
            const removed = await withSessionLock(() => {
                const raw = localStorage.getItem(storageKey);
                if (!raw || JSON.parse(raw).sessionGeneration !== generation)
                    return false;
                localStorage.removeItem(storageKey);
                return true;
            });
            if (removed)
                notifySessionCleared();
        };
    }
    /* ------------------------------------------------------------------ */
    /* GET                                                                */
    /* ------------------------------------------------------------------ */
    static async getSession() {
        var _a;
        if (typeof window === "undefined")
            return null;
        const sessionObj = await this.readSessionWithGeneration();
        if (!sessionObj)
            return null;
        const generation = (_a = sessionObj.sessionGeneration) !== null && _a !== void 0 ? _a : null;
        /* ---------- validate app-id ---------- */
        const config = await getConfig();
        if (sessionObj.appId && sessionObj.appId !== config.appId) {
            await this.clearSessionIfGeneration(generation);
            return null;
        }
        /* ---------- check JWT expiration ---------- */
        // Deliberately NO skew buffer here, unlike the per-request freshness helper.
        // This is the restore path: at boot a token already past `exp` is unusable
        // either way, so clearing it is honest. Preserving it would only make
        // isAuthenticated() lie about a session that cannot authenticate anything.
        try {
            // Check expiry on the ID TOKEN — it is ALWAYS a JWT. The OIDC access_token for
            // email/social (hosted) logins is an OPAQUE string (not a JWT), so decoding IT
            // here threw and the catch below wiped a perfectly valid session — breaking
            // hosted login. The idToken carries the canonical identity (@user.id) + exp.
            const { idToken } = sessionObj;
            const { exp } = JSON.parse(this.decodeBase64Url(idToken.split(".")[1]));
            // Treat a MISSING or non-numeric expiry as EXPIRED (fail closed). A bare
            // `Date.now() > exp * 1000` is NaN -> false when exp is absent/non-numeric,
            // so a forged or malformed token with no usable expiry would otherwise be
            // accepted as valid forever.
            const expired = typeof exp !== "number" || !Number.isFinite(exp) || Date.now() > exp * 1000;
            if (expired) {
                const { refreshToken } = sessionObj;
                // Expired token with no way to refresh — clear the stale session
                // (mirrors the refresh-failed branch below) so isAuthenticated()
                // does not stay true and every request does not 401 + retry forever.
                if (!refreshToken) {
                    await this.clearSessionIfGeneration(generation);
                    return null;
                }
                let refreshed;
                try {
                    refreshed = await refreshSession(refreshToken, sessionObj.issuer);
                }
                catch (refreshErr) {
                    // DS4-0054: only a DEFINITIVE server rejection (401/403) proves the
                    // refresh token is dead. A transport failure - offline, DNS, timeout,
                    // 5xx - is unknown, not proof the session is invalid. Destroying local
                    // state here signs the user out on a network blip (common on mobile),
                    // so RETAIN it and return the existing session; the next request will
                    // retry the refresh. The retention is BOUNDED by the token's own
                    // expiry plus a grace window, so a revoked session - or an issuer 401
                    // a gateway rewrote into a 5xx - cannot keep isAuthenticated() true
                    // forever on a device that never reaches the issuer again.
                    if (isDefinitiveRejection(refreshErr) || !mayRetainAfterTransportFailure(exp)) {
                        await this.clearSessionIfGeneration(generation);
                        return null;
                    }
                    return { address: sessionObj.address, session: sessionObj };
                }
                if ((refreshed === null || refreshed === void 0 ? void 0 : refreshed.idToken) && (refreshed === null || refreshed === void 0 ? void 0 : refreshed.accessToken)) {
                    const committed = await this.commitRefreshedTokens({ generation, idToken: sessionObj.idToken }, refreshed.idToken, refreshed.accessToken, 
                    // Persist the ROTATED refresh token — the old one is now
                    // single-use spent; reusing it would revoke the family.
                    refreshed.refreshToken);
                    // Superseded means a login or logout landed while we were on the
                    // network. Returning our own stale null here would sign the UI out
                    // of a session that is now perfectly valid, because init() applies
                    // whatever restoreSession() returns. Re-read once and report THAT.
                    if (committed.outcome === "superseded") {
                        const current = this.readRaw();
                        if (!current)
                            return null;
                        return { address: current.address, session: current };
                    }
                    const newObj = committed.session;
                    if (!newObj)
                        return null;
                    return { address: newObj.address, session: newObj };
                }
                // The server answered but issued no usable tokens - a definitive refusal,
                // not a transport failure - so clear the stale session to stop retry loops,
                // but only if it is still the session we set out to refresh.
                await this.clearSessionIfGeneration(generation);
                return null;
            }
        }
        catch (err) {
            // Token decode or refresh failed — clear stale session to prevent retry loops
            await this.clearSessionIfGeneration(generation);
            return null;
        }
        return { address: sessionObj.address, session: sessionObj };
    }
    /* ------------------------------------------------------------------ */
    /* CLEAR                                                              */
    /* ------------------------------------------------------------------ */
    /**
     * Remove the session unconditionally (logout, app-id mismatch, dead refresh).
     *
     * ASYNC AND LOCKED, and callers MUST await it. Two failure modes hang on this:
     *
     *  - Removing outside the lock lets a refresh that read before the logout write
     *    the session back afterwards, resurrecting a logged-out session.
     *  - Not awaiting lets `logout()` return while the credential is still in
     *    storage, so the very next request authenticates as the user who just left.
     */
    static async clearSession() {
        if (typeof window === "undefined")
            return;
        ++this.establishmentEpoch;
        const key = this.BOUNDED_SESSION_STORAGE_KEY;
        const logoutKey = this.logoutKey();
        const removed = await withSessionLock(() => {
            localStorage.setItem(logoutKey, mintSessionGeneration());
            if (localStorage.getItem(key) === null)
                return false;
            localStorage.removeItem(key);
            return true;
        });
        // Only announce a clear that actually happened, so a no-op cannot sign out
        // a UI that was already signed out.
        if (removed)
            notifySessionCleared();
    }
    /**
     * Remove the session only if it is still the one identified by `generation`.
     *
     * Used by every failure path that wants to drop a dead session without
     * trampling a newer login that landed while the failure was in flight.
     */
    static async clearSessionIfGeneration(generation) {
        if (typeof window === "undefined")
            return;
        const removed = await withSessionLock(() => {
            var _a;
            const current = this.readRaw();
            if (!current)
                return false;
            // No captured generation means no proof this is the session we meant to
            // drop. Leave it alone rather than risk removing a replacement.
            if (generation === null)
                return false;
            if (((_a = current.sessionGeneration) !== null && _a !== void 0 ? _a : null) !== generation)
                return false;
            localStorage.removeItem(this.BOUNDED_SESSION_STORAGE_KEY);
            return true;
        });
        if (removed)
            notifySessionCleared();
    }
    /* ------------------------------------------------------------------ */
    /* IS-AUTH                                                            */
    /* ------------------------------------------------------------------ */
    static isAuthenticated() {
        // NOTE: this remains synchronous to stay backward compatible
        return !!localStorage.getItem(this.BOUNDED_SESSION_STORAGE_KEY);
    }
    /* ------------------------------------------------------------------ */
    /* TOKEN HELPERS                                                      */
    /* ------------------------------------------------------------------ */
    // These reads are DEFENSIVE (try/catch -> treat a corrupt blob as absent).
    // logout() reads the refresh token + issuer BEFORE the session is cleared; if a
    // corrupt (non-JSON) blob threw out of the read, logout would abort with the
    // credential still in storage while isAuthenticated() kept reporting true, so
    // sign-out could never complete. A corrupt blob is unusable either way, so
    // reporting "no token" lets the clear path proceed and finish the sign-out.
    static getIdToken() {
        if (typeof window === "undefined")
            return null;
        try {
            const session = localStorage.getItem(this.BOUNDED_SESSION_STORAGE_KEY);
            return session ? JSON.parse(session).idToken : null;
        }
        catch (_a) {
            return null;
        }
    }
    static getRefreshToken() {
        if (typeof window === "undefined")
            return null;
        try {
            const session = localStorage.getItem(this.BOUNDED_SESSION_STORAGE_KEY);
            return session ? JSON.parse(session).refreshToken : null;
        }
        catch (_a) {
            return null;
        }
    }
    /** The issuer base that minted this session (for routing silent refresh). */
    static getIssuer() {
        var _a;
        if (typeof window === "undefined")
            return null;
        try {
            const session = localStorage.getItem(this.BOUNDED_SESSION_STORAGE_KEY);
            return session ? ((_a = JSON.parse(session).issuer) !== null && _a !== void 0 ? _a : null) : null;
        }
        catch (_b) {
            return null;
        }
    }
    /* ------------------------------------------------------------------ */
    /* UPDATE TOKENS                                                      */
    /* ------------------------------------------------------------------ */
    /**
     * Commit refreshed tokens, bound to the session generation they were minted for.
     *
     * Returns WHICH of the four outcomes happened, and the session the caller must
     * actually use. The old version returned void and silently dropped a refused
     * write, so `getFreshAuthToken` handed back the refreshed token regardless — a
     * request could then execute as the previous user while storage and the UI had
     * already moved to a new one.
     *
     * Never hand back `idToken` directly: use `result.session`.
     */
    static async commitRefreshedTokens(expected, idToken, accessToken, refreshToken) {
        if (typeof window === "undefined")
            return { outcome: "superseded", session: null };
        // Resolve config BEFORE taking the lock: no awaits are allowed inside the
        // critical section, or another realm could interleave between read and write.
        const config = await getConfig();
        const result = await withSessionLock(() => {
            var _a;
            // Read INSIDE the lock. The snapshot the caller captured before its
            // network refresh is only a comparison key; what gets written is decided
            // against what storage holds right now.
            const current = this.readRaw();
            /* ---------- app-id guard ---------- */
            if ((current === null || current === void 0 ? void 0 : current.appId) && current.appId !== config.appId) {
                localStorage.removeItem(this.BOUNDED_SESSION_STORAGE_KEY);
                return { outcome: "superseded", session: null, cleared: true };
            }
            const next = Object.assign(Object.assign(Object.assign({}, (current !== null && current !== void 0 ? current : {})), { idToken,
                accessToken }), (typeof refreshToken === "string" && refreshToken.length > 0
                ? { refreshToken }
                : {}));
            const outcome = decideCommit(current, expected, next);
            if (outcome === "superseded")
                return { outcome, session: null };
            // Another caller's refresh of this same login already landed. Theirs is
            // the truth; reporting a principal change here would fail every ordinary
            // concurrent request at expiry, which is the common case.
            if (outcome === "same-generation-winner")
                return { outcome, session: current };
            if (outcome === "already-equal")
                return { outcome, session: current };
            /* ---------- cross-principal guard (#221) ---------- */
            // Belt-and-braces alongside the generation check: refuse to graft a
            // refreshed token onto a DIFFERENT wallet's stored session. Generations
            // catch a same-wallet re-login, which this address check cannot.
            if (!refreshedTokenMayReplaceStored(idToken, current.address, (s) => this.decodeBase64Url(s))) {
                console.warn("[WebSessionManager] commitRefreshedTokens: refreshed token wallet does not match the stored session address — discarding stale refresh");
                return { outcome: "superseded", session: null };
            }
            (_a = next.appId) !== null && _a !== void 0 ? _a : (next.appId = config.appId);
            this.writeRaw(next);
            return { outcome, session: next };
        });
        if (result.cleared)
            notifySessionCleared();
        return { outcome: result.outcome, session: result.session };
    }
}
WebSessionManager.establishmentEpoch = 0;
